import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

// ScrollProgress: A thin progress bar at the top indicating scroll position
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const update = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress((window.scrollY / total) * 100);
    };
    window.addEventListener("scroll", update);
    return () => window.removeEventListener("scroll", update);
  }, []);
  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-blue-200 z-50">
      <div
        className="h-full bg-blue-600 transition-all duration-200"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// SectionCard: Animated card wrapper for each section (h3 + content)
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03, y: -5 }}
    transition={{
      duration: 0.4,
      ease: "easeOut",
      type: "spring",
      stiffness: 200,
      damping: 20,
    }}
    viewport={{ once: true, amount: 0.2 }}
    className="w-full md:w-1/2 p-6 bg-white/80 backdrop-blur-sm shadow-lg border border-[var(--color-secondary-muted)] rounded"
  >
    <h3 className="text-xl font-bold text-blue-900 mb-3">{title}</h3>
    {children}
  </motion.div>
);

// Paragraph: Styled paragraph component for consistent text appearance
const Paragraph = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p
    className={`text-base text-gray-800 leading-relaxed text-justify ${className}`}
  >
    {children}
  </p>
);

const Staking = () => (
  <div className="relative overflow-hidden space-y-8 px-4 py-10 md:py-6 md:px-8">
    <ScrollProgress />

    {/* Introduction */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        ease: "easeOut",
        type: "spring",
        stiffness: 200,
        damping: 20,
      }}
      viewport={{ once: true }}
      className="max-w-6xl mx-auto space-y-4 text-center"
    >
      <h2 className="text-2xl font-bold text-primary-dark">
        Staking Mechanism for NAU, NAUY, and NAUN
      </h2>
      <Paragraph className="text-gray-700 max-w-6xl mx-auto">
        The staking mechanism is a secure, fair, and independent rewards system
        deployed across three distinct smart contracts, each corresponding to
        one of the tokens:
      </Paragraph>
      <ul className="list-disc list-inside text-base text-gray-800 max-w-6xl mx-auto">
        <li>NAU Staking → Earns NAU rewards</li>
        <li>NAUY Staking → Earns NAUY rewards</li>
        <li>NAUN Staking → Earns NAUN rewards</li>
      </ul>
      <Paragraph className="max-w-6xl mx-auto">
        Each contract is isolated to ensure separation of concerns and
        protection from cross-token interference, supporting transparency,
        security, and upgradability.
      </Paragraph>
    </motion.div>

    {/* Two-column layout */}
    <div className="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto">
      {/* Left block: Security, How Staking Works */}
      <SectionCard title="Security First">
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            Immutable Tokens: Only NAU, NAUY and NAUN can be
            staked.
          </li>
          <li>
            Role-Based Access Control: Uses AccessControl with roles
            DEFAULT_ADMIN_ROLE, FUNDER_ROLE, RATE_SETTER_ROLE.
          </li>
          <li>
            ReentrancyGuard: stake, unstake, and claimRewards protected from
            reentrancy.
          </li>
          <li>
            Unstaking Fee: 2% fee on every unstake. No fee for staking.
          </li>
          <li>
            Lockup Period: 7-day lockup from initial stake prevents flash
            farming.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-blue-900 mt-6 mb-3">
          How Staking Works
        </h3>

        <Paragraph className="font-semibold">Stake</Paragraph>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            User calls <code>stake(uint256 amount)</code>.
          </li>
          <li>First deposit starts a 7-day lockup timer.</li>
          <li>Tokens are held; user balance updates accordingly.</li>
        </ul>

        <Paragraph className="font-semibold">Earn Rewards</Paragraph>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            Rewards accrue at a dynamic <code>rewardRate</code>.
          </li>
          <li>
            Uses cumulative <code>rewardPerTokenStored</code> metric for fair
            calculation.
          </li>
          <li>
            Users call <code>claimRewards()</code> to claim without unstaking.
          </li>
        </ul>

        <Paragraph className="font-semibold">Unstake</Paragraph>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>After 7 days, users can unstake any amount.</li>
          <li>
            2% fee deducted and sent to <code>feeRecipient</code>.
          </li>
          <li>Remaining tokens returned to the user.</li>
        </ul>

        <Paragraph className="font-semibold">Claim Rewards</Paragraph>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>Rewards paid in the staked token.</li>
          <li>
            2% fee on rewards sent to <code>feeRecipient</code>.
          </li>
          <li>Can claim at any time, even before lockup ends.</li>
        </ul>
      </SectionCard>

      {/* Right block: Fairness, Governance, Summary Table, Why This Matters */}
      <SectionCard title="Fairness and Governance">
        <Paragraph>
          The <code>rewardRate</code> is updateable on-chain by addresses with{" "}
          <code>RATE_SETTER_ROLE</code>. This allows the DAO or controller to
          rebalance incentives, ensuring equity across pools, responsiveness to
          supply/demand, and long-term sustainability.
        </Paragraph>

        <h3 className="text-xl font-bold text-blue-900 mt-6 mb-3">
          Reward Funding and Surplus Recovery
        </h3>
        <ul className="list-disc list-inside text-base text-gray-800">
          <li>
            Funding via <code>fundRewards(uint256 amount)</code> by{" "}
            <code>FUNDER_ROLE</code>.
          </li>
          <li>
            After 3-year program, excess unclaimed rewards recoverable by admin
            via <code>recoverExcessRewardTokens(...)</code>.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-blue-900 mt-6 mb-3">
          Summary Table
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-base text-gray-800">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left font-semibold">Token</th>
                <th className="px-4 py-2 text-left font-semibold">
                  Stake to Earn
                </th>
                <th className="px-4 py-2 text-left font-semibold">
                  Unstaking Fee
                </th>
                <th className="px-4 py-2 text-left font-semibold">
                  Reward Fee
                </th>
                <th className="px-4 py-2 text-left font-semibold">Lockup</th>
                <th className="px-4 py-2 text-left font-semibold">Duration</th>
              </tr>
            </thead>
            <tbody>
              {[
                {
                  token: "NAU",
                  fee: "2%",
                  lockup: "7 days",
                  duration: "3 years",
                },
                {
                  token: "NAUY",
                  fee: "2%",
                  lockup: "7 days",
                  duration: "3 years",
                },
                {
                  token: "NAUN",
                  fee: "2%",
                  lockup: "7 days",
                  duration: "3 years",
                },
              ].map(({ token, fee, lockup, duration }) => (
                <tr className="border-t" key={token}>
                  <td className="px-4 py-2">{token}</td>
                  <td className="px-4 py-2">{token}</td>
                  <td className="px-4 py-2">{fee}</td>
                  <td className="px-4 py-2">{fee}</td>
                  <td className="px-4 py-2">{lockup}</td>
                  <td className="px-4 py-2">{duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="text-xl font-bold text-blue-900 mt-6 mb-3">
          Why This Matters
        </h3>
        <Paragraph className="text-gray-700 max-w-6xl mx-auto">
          The staking system is the core trust engine of the platform—it ensures
          committed participants influence governance via staked NAU, fuels
          opinion mining via NAUY/NAUN, and aligns long-term value creation with
          active contribution.
        </Paragraph>
      </SectionCard>
    </div>
  </div>
);

export default Staking;
